import { useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, Stack, Grid, Alert, Link, Tooltip, CircularProgress,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  readProgress, PROGRESS_KIND, PROGRESS_COLOR, PRODUCT_SOURCE_LABEL,
} from '../../utils/projectProgress';

// 영역 D — ProductDetailPage 의 "관련 Projects".
//
// FEAT-018 — 출처를 프로젝트 차터(project-registry)로 교체했다.
// 이전에는 콘솔이 자체적으로 들고 있던 projects-meta 를 읽었는데, 그 데이터는
// 현재 진행 중인 차터와 한 건도 대응되지 않아 제품 담당자가 자기 제품에서
// 지금 돌아가는 프로젝트를 볼 수 없었다.
//
// 연결 규칙은 서버(projects/board)가 판정한다 — 차터에 적힌 근거만 쓰고
// 추측하지 않는다. 여기서는 그 결과를 이 제품 기준으로 거르고, 진행 현황을
// GitHub Project 기준으로 보여줄 뿐이다.
//
// Props:
//   productId         : 본 페이지의 Product.id (예: 'prd-rayguard')
//   items             : /api/projects/board 의 items[] (전체 — 여기서 제품별로 거른다)
//   loading / error   : 로딩·오류 상태 (다른 영역과 동일한 분리 패턴)
//   progressAvailable : GitHub Project 조회 가능 여부. false 면 진행 자리만 비운다
//   progressError     : 위 실패 사유

// 차터 폴더 = 상태. 진행 중인 것을 위로.
const STATUS_ORDER = ['active', 'backlog', 'paused', 'done'];
const STATUS_HEADER = {
  active: '🛠 진행 중',
  backlog: '📝 대기',
  paused: '⏸ 일시중단',
  done: '✅ 완료',
};
const STATUS_COLOR = {
  active: '#3fb950',
  backlog: '#d29922',
  paused: '#a371f7',
  done: '#8b949e',
};

function ProgressBar({ prog }) {
  const color = PROGRESS_COLOR[prog.kind];
  return (
    <Tooltip title={prog.detail} arrow>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
        <Box sx={{
          flexGrow: 1, height: 6, bgcolor: 'action.hover',
          border: '1px solid', borderColor: 'divider', overflow: 'hidden',
        }}>
          {prog.kind === PROGRESS_KIND.OK ? (
            <Box sx={{ width: `${prog.percent}%`, height: '100%', bgcolor: color }} />
          ) : (
            // 진행을 모르는 상태는 채우지 않는다 — 0% 로 칠하면 '시작 전' 과 뒤섞인다.
            <Box sx={{
              width: '100%', height: '100%',
              backgroundImage: `repeating-linear-gradient(45deg, ${color}55 0 4px, transparent 4px 8px)`,
            }} />
          )}
        </Box>
        <Typography variant="caption" sx={{ color, fontWeight: 600, whiteSpace: 'nowrap', fontSize: 11 }}>
          {prog.label}
        </Typography>
      </Box>
    </Tooltip>
  );
}

export default function RelatedProjectsList({
  productId,
  items = [],
  loading = false,
  error = null,
  progressAvailable = true,
  progressError = null,
}) {
  const { grouped, totalCount } = useMemo(() => {
    const mine = items.filter((it) => (it.products || []).some((p) => p.id === productId));
    const g = Object.fromEntries(
      STATUS_ORDER.map((s) => [s, mine.filter((r) => r.status === s)]),
    );
    return { grouped: g, totalCount: mine.length };
  }, [items, productId]);

  if (loading) {
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <CircularProgress size={16} />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>프로젝트 현황을 불러오는 중…</Typography>
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ fontSize: '0.88em' }}>
        프로젝트 현황을 불러오지 못했습니다 — {error}
      </Alert>
    );
  }

  if (totalCount === 0) {
    return (
      <Alert severity="info" sx={{ fontSize: '0.88em' }}>
        이 제품에 연결된 프로젝트가 없습니다. 연결은 <b>프로젝트 차터</b>를 근거로만 맺어집니다 —
        차터의 「본부 프로젝트 보관소 등록」에 이 제품을 명시하거나, 「주요 링크」에 이 제품의
        저장소 주소를 적으면 자동으로 여기에 나타납니다.
      </Alert>
    );
  }

  return (
    <Stack spacing={2.5}>
      {!progressAvailable && (
        <Alert severity="warning" sx={{ fontSize: '0.85em' }}>
          GitHub Project 진행 현황을 읽지 못했습니다 — 프로젝트 목록·일정은 정상이며 진행률만 비어 있습니다.
          {progressError ? ` (${progressError})` : ''}
        </Alert>
      )}
      {STATUS_ORDER.map((status) => {
        const rows = grouped[status];
        if (!rows || rows.length === 0) return null;
        return (
          <Box key={status}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                {STATUS_HEADER[status]}
              </Typography>
              <Chip
                label={rows.length}
                size="small"
                sx={{ minWidth: 28, bgcolor: STATUS_COLOR[status], color: '#0d1117', fontWeight: 600 }}
              />
            </Stack>
            <Grid container spacing={1.5}>
              {rows.map((r) => {
                const prog = readProgress(r);
                return (
                  <Grid item xs={12} sm={6} md={4} key={r.slug}>
                    <Card variant="outlined" sx={{ height: '100%', borderRadius: 0 }}>
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={r.title}
                        >
                          {r.title}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
                        >
                          {r.owner || '담당 미지정'}
                          {(r.startRaw || r.endRaw) && ` · ${r.startRaw || 'TBD'} ~ ${r.endRaw || 'TBD'}`}
                        </Typography>

                        <ProgressBar prog={prog} />

                        {prog.kind === PROGRESS_KIND.OK && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5, fontSize: 11 }}>
                            완료 {prog.counts.done} · 진행 {prog.counts.inProgress} · 할 일 {prog.counts.todo}
                            {prog.counts.other ? ` · 미분류 ${prog.counts.other}` : ''}
                          </Typography>
                        )}

                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ gap: 0.5, mt: 0.75 }}>
                          {r.productSource && (
                            <Tooltip title="이 제품에 연결된 근거 — 차터를 고치면 연결도 바뀝니다" arrow>
                              <Chip
                                label={PRODUCT_SOURCE_LABEL[r.productSource] || r.productSource}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: 10, height: 20 }}
                              />
                            </Tooltip>
                          )}
                          {r.githubProject && (
                            <Link
                              href={r.githubProject.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ display: 'flex', alignItems: 'center', gap: 0.3, fontSize: 11 }}
                            >
                              Project <OpenInNewIcon sx={{ fontSize: 12 }} />
                            </Link>
                          )}
                          <Link
                            href={r.htmlUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.3, fontSize: 11 }}
                          >
                            차터 <OpenInNewIcon sx={{ fontSize: 12 }} />
                          </Link>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        );
      })}
    </Stack>
  );
}
